import { uiLabel } from '../utils/labels.js'

/**
 * The one door to the camera and the microphone.
 *
 * ── Why there is only one ────────────────────────────────────────────────────
 *
 * There were two. `UiAudioRecorder` asked for the microphone and
 * `modules/inventory/CodeScanner.vue` asked for the camera, each with its own
 * `getUserMedia`, its own reading of what the browser answered, and its own
 * teardown. That is the shape the eight `multer` doors had before
 * `limitedUpload` (§4.4): duplicated not because anybody wanted two, but
 * because asking a browser for a device looks like three lines until you count
 * the ways it can say no.
 *
 * And the two had already drifted:
 *
 *   · neither said anything when the answer was «not on an insecure page»,
 *     which is what a phone answers on `http://192.168.x.x` — the exact device
 *     and the exact address a crew uses. The button was simply grey, with no
 *     text, and `localhost` hides it from whoever is building the screen;
 *   · «no permission», «no microphone» and «something else is using the camera»
 *     were collapsed into one sentence, so people were sent looking in the
 *     wrong place;
 *   · one worded its refusals through the ui-kit and the other through its own
 *     module's i18n — the same refusal, translated twice, free to diverge.
 *
 * ── What it decides, and what it deliberately does not ───────────────────────
 *
 * It decides everything about REACHING the device: whether it can be asked at
 * all, what a refusal means, and that the tracks get stopped afterwards. It
 * decides nothing about what you do with the stream — `MediaRecorder` stays in
 * the recorder and `BarcodeDetector` stays in inventory, because those are two
 * different jobs and neither belongs to the platform.
 */

/** Why the device cannot even be asked for. */
export const BLOCKED_INSECURE = 'insecure'
export const BLOCKED_UNSUPPORTED = 'unsupported'

/** How a browser refuses once it HAS been asked. */
export const REFUSED_DENIED = 'denied'
export const REFUSED_MISSING = 'missing'
export const REFUSED_BUSY = 'busy'
export const REFUSED_UNKNOWN = 'unknown'

/** A refusal, named. Carries the original so a caller can still log it. */
export class MediaCaptureError extends Error {
  constructor(reason, cause = null) {
    super(`The device was not available: ${reason}`)
    this.name = 'MediaCaptureError'
    this.reason = reason
    this.cause = cause
  }
}

/**
 * What the browser's own error names mean.
 *
 * Written out rather than collapsed into «denied or not», because these need
 * three different sentences: one is fixed by tapping «allow», one by using
 * another device, and one by closing whatever else is holding the camera.
 * Telling somebody the wrong one sends them looking in the wrong place, and
 * that is what the two implementations this replaces both did.
 */
const REFUSALS = {
  NotAllowedError: REFUSED_DENIED,
  PermissionDeniedError: REFUSED_DENIED,
  SecurityError: REFUSED_DENIED,
  NotFoundError: REFUSED_MISSING,
  DevicesNotFoundError: REFUSED_MISSING,
  OverconstrainedError: REFUSED_MISSING,
  NotReadableError: REFUSED_BUSY,
  TrackStartError: REFUSED_BUSY,
  AbortError: REFUSED_BUSY,
}

// `unknown` lands on the same wording as `unsupported`, which is what both
// callers did with everything that was not a denial before this file existed.
// Saying «this device cannot» about something transient is a small lie; saying
// nothing at all is a bigger one.
const WORDING = {
  [BLOCKED_INSECURE]: 'Insecure',
  [BLOCKED_UNSUPPORTED]: 'Unsupported',
  [REFUSED_DENIED]: 'Denied',
  [REFUSED_MISSING]: 'Missing',
  [REFUSED_BUSY]: 'Busy',
  [REFUSED_UNKNOWN]: 'Unsupported',
}

/**
 * Whether this page may ask at all, and why not.
 *
 * `navigator.mediaDevices` is **undefined** outside a secure context, so the
 * naive check — «is getUserMedia there?» — cannot tell «this browser is too
 * old» from «you are on http». They need different sentences, and the second is
 * the one that actually happens: `isSecureContext` is true on `localhost` and
 * false on `http://192.168.x.x`, so it works on the machine of whoever builds
 * the screen and fails on the phone it was built for.
 */
export function blockedBecause(device = 'audio') {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return BLOCKED_UNSUPPORTED
  if (window.isSecureContext === false) return BLOCKED_INSECURE
  if (device === 'screen') return typeof navigator.mediaDevices?.getDisplayMedia === 'function' ? '' : BLOCKED_UNSUPPORTED
  if (!navigator.mediaDevices?.getUserMedia) return BLOCKED_UNSUPPORTED
  return ''
}

/**
 * @param {object} [options]
 * @param {'audio'|'camera'|'screen'} [options.device]  which set of words to use;
 *   `screen` asks for the screen (getDisplayMedia) instead of a device — the
 *   same door, so sharing a screen is refused and released the same way. A
 *   phone answers `unsupported`: mobile browsers do not share the screen.
 */
export function useMediaCapture({ device = 'audio' } = {}) {
  // Plain values and not `computed`: neither answer can change while the page
  // is open — a tab does not become a secure context — and a computed would
  // promise a reactivity that does not exist.
  const blockedBy = blockedBecause(device)
  const available = !blockedBy

  /**
   * The sentence to put on screen, in the person's own language.
   *
   * It names the DEVICE, because a screen can hold both and «permission was
   * refused» without saying for what is a message somebody has to guess at.
   */
  const problemLabel = (reason) => uiLabel(`${device}${WORDING[reason] ?? 'Unsupported'}`)

  /**
   * Asks for the device.
   *
   * @param {MediaStreamConstraints} constraints
   * @returns {Promise<MediaStream>}
   * @throws {MediaCaptureError} with `.reason` — never a raw DOMException, so
   *         no caller ever has to know the browser's spelling again
   */
  async function capture(constraints) {
    if (blockedBy) throw new MediaCaptureError(blockedBy)

    try {
      return device === 'screen'
        ? await navigator.mediaDevices.getDisplayMedia(constraints)
        : await navigator.mediaDevices.getUserMedia(constraints)
    } catch (error) {
      throw new MediaCaptureError(REFUSALS[error?.name] ?? REFUSED_UNKNOWN, error)
    }
  }

  /**
   * Gives it back. Every track, always.
   *
   * Until the last one is stopped the browser keeps the recording indicator
   * lit, and to somebody holding a phone that reads as «it is still
   * listening» — which for a microphone is the difference between a tool and
   * something nobody opens twice.
   */
  function release(stream) {
    for (const track of stream?.getTracks?.() ?? []) track.stop()
  }

  return { available, blockedBy, capture, release, problemLabel }
}
